import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EventsService } from '../../@shared/services/events-service';
import { Horses } from '../../@shared/services/horses';
import { StaticService } from '../../@shared/services/static-service';
import { FamilyService } from '../../@shared/services/family-service';
import { MembershipService } from '../../@shared/services/membership-service';
import { StorageService } from '../../@core/services/storage.service';
import { ToastService } from '../../@core/services/toast.service';
import { PaymentService } from '../../@shared/services/payment.service';
import { TrainingService } from '../../@shared/services/training.service';
import {
  EventClassResponse,
  EventContactResponse,
  EventResponse,
  EventServiceResponse,
  EventSponsorResponse,
  FamilyMemberResponse,
  HorseResponse,
  HorseRegistryResponse,
  TrainingPackageResponse,
  PriceInfoResponse,
  UserPricesResponse
} from '../../@shared/models/response.module';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { MembershipPurchaseModal } from '../../@shared/components/membership-purchase-modal/membership-purchase-modal';
import { CustomPaymentModal } from '../../@shared/components/custom-payment-modal/custom-payment-modal';
import { CompanyService } from '../../@shared/services/company.service';
import { CompanyResponse } from '../../@shared/models/response.module';
import { AnalyticsService } from '../../@core/services/analytics.service';

interface ClassRegistration {
  event_class_id: number;
  class_name: string;
  user_id: string;
  user_name?: string;
  horse_id: number | null;
  horse_name?: string;
  price: number;
  base_price: number;
  effective_price: number;
  price_currency: string;
  error?: string;
  expanded?: boolean;
  notes?: string;
}

interface ServiceRegistration {
  event_service_id: number;
  service_name: string;
  user_id: string;
  user_name?: string;
  num_registrations: number;
  price_per_unit: number;
  base_price_per_unit: number;
  effective_price_per_unit: number;
  price_currency: string;
  expanded?: boolean;
  max_slots?: number;
  notes?: string;
}

interface PackageRegistration {
  package_id: number;
  package_name: string;
  user_id: string;
  user_name?: string;
  horse_id: number | null;
  horse_name?: string;
  session_count: number;
  session_type: 'group' | 'individual' | 'both';
  price: number;
  base_price: number;
  effective_price: number;
  price_currency: string;
  expanded?: boolean;
  notes?: string;
}

@Component({
  selector: 'app-event-registration',
  standalone: false,
  templateUrl: './event-registration.html',
  styleUrls: ['./event-registration.css']
})
export class EventRegistrationComponent implements OnInit {
  eventId!: number;
  event: EventResponse | null = null;
  company: CompanyResponse | null = null;
  eventClasses: EventClassResponse[] = [];
  eventServices: EventServiceResponse[] = [];
  eventSponsors: EventSponsorResponse[] = [];
  eventContacts: EventContactResponse[] = [];
  allHorses: HorseResponse[] = [];
  familyMembers: {user_id: string, name: string}[] = [];

  currentUserId!: string;
  currentUserName!: string;

  classRegistrations: ClassRegistration[] = [];
  serviceRegistrations: ServiceRegistration[] = [];
  packageRegistrations: PackageRegistration[] = [];

  // Training packages
  trainingPackages: TrainingPackageResponse[] = [];
  isLoadingPackages: boolean = false;

  // Membership pricing
  priceInfoMap: Map<string, PriceInfoResponse> = new Map();
  userPlanIds: number[] = [];
  isLoadingPrices: boolean = false;

  addHorseForm!: FormGroup;
  showAddHorseModal: boolean = false;
  showTermsModal: boolean = false;
  descriptionExpanded: boolean = false;
  isLoadingHorses: boolean = false;
  isLoadingEvent: boolean = false;
  isLoadingClasses: boolean = false;
  isLoadingServices: boolean = false;
  isLoadingSponsors: boolean = false;
  isLoadingContacts: boolean = false;
  isLoadingCompany: boolean = false;
  isSubmitting: boolean = false;

  horseRegistries: HorseRegistryResponse[] = [];
  horseGenders: {id: number, name: string}[] = [];

  // Horse registry search
  showHorseRegistryModal: boolean = false;
  horseSearchQuery: string = '';
  horseSearchResults: HorseResponse[] = [];
  isSearchingHorses: boolean = false;
  activeHorseRegistration: any = null; // The registration we're selecting a horse for

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private eventsService: EventsService,
    private horsesService: Horses,
    private staticService: StaticService,
    private familyService: FamilyService,
    private membershipService: MembershipService,
    private storageService: StorageService,
    private toastService: ToastService,
    private paymentService: PaymentService,
    private trainingService: TrainingService,
    private companyService: CompanyService,
    private modalService: NgbModal,
    private fb: FormBuilder,
    private translateService: TranslateService,
    private analyticsService: AnalyticsService
  ) {
    this.initAddHorseForm();
  }

  ngOnInit(): void {
    console.log('EventRegistrationComponent initialized');
    const user = this.storageService.getUser();
    console.log('Current user:', user);
    this.currentUserId = user.user_id;
    this.currentUserName = user.full_name || `${user.first_name} ${user.last_name}`;

    // Initialize horse genders with correct mapping
    this.initHorseGenders();
    this.translateService.onLangChange.subscribe(() => {
      this.initHorseGenders();
    });

    this.route.params.subscribe(params => {
      this.eventId = +params['eventId'];
      console.log('Event ID from route:', this.eventId);
      this.loadEventData();
      this.loadEventClasses();
      this.loadEventServices();
      // Note: loadEventSponsors and loadEventContacts are called inside loadEventData
      // after the event is loaded, so we can use the event organizer's company_id
      this.loadAllHorses();
      this.loadHorseRegistries();
      this.loadFamilyMembers();
      this.loadUserPrices();
    });

    // Check for payment result query params
    this.route.queryParams.subscribe(queryParams => {
      if (queryParams['membership_payment'] === 'success') {
        this.toastService.success('Membership purchased successfully! Your member discounts are now active.');
        // Reload user prices to get updated discounts
        this.loadUserPrices();
        // Clean up the URL
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true
        });
      } else if (queryParams['membership_payment'] === 'cancelled') {
        this.toastService.info('Membership purchase was cancelled.');
        // Clean up the URL
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true
        });
      } else if (queryParams['cancelled'] === 'true') {
        // User cancelled Stripe checkout - show info toast
        this.toastService.info('Payment was cancelled. Your registration was not completed.');
        // Clean up the URL
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true
        });
      }
    });
  }

  // Image files for horse creation
  horseImageFile: File | null = null;
  pedigreeFile: File | null = null;
  vaccinationCardFile: File | null = null;

  // Image preview URLs
  horseImagePreview: string | null = null;
  pedigreePreview: string | null = null;
  vaccinationCardPreview: string | null = null;

  // Form validation state
  addHorseSubmitted: boolean = false;

  initAddHorseForm(): void {
    this.addHorseForm = this.fb.group({
      name: ['', Validators.required],
      date_of_birth: ['', Validators.required],
      passport_id: ['', Validators.required],
      horse_registry_id: [null],
      gender: [null, Validators.required],
      breed: [''],
      color: [''],
      vaccination_date: [''],
      next_vaccination_date: ['']
    });
  }

  private initHorseGenders(): void {
    // Gender IDs must match backend: 0=Gelding, 1=Stallion, 2=Mare
    this.horseGenders = [
      { id: 0, name: this.translateService.instant('horses.gender.gelding') },
      { id: 1, name: this.translateService.instant('horses.gender.stallion') },
      { id: 2, name: this.translateService.instant('horses.gender.mare') }
    ];
  }

  loadEventData(): void {
    this.isLoadingEvent = true;
    this.eventsService.getEvents({ event_id: this.eventId }).subscribe({
      next: (response) => {
        if (response.data && response.data.length > 0) {
          this.event = response.data[0];

          // Track registration flow start with Google Analytics
          this.analyticsService.trackEventRegistrationStart(
            this.eventId,
            this.event.event_name || 'Unknown Event'
          );

          // Load training packages if this is a training event
          if (this.isTrainingEvent) {
            this.loadTrainingPackages();
          }
          // Load company data for custom payment check
          if (this.event.company_id) {
            this.loadCompanyData(this.event.company_id);
          }
          // Load sponsors and contacts using the event organizer's company_id
          this.loadEventSponsors(this.event.company_id || undefined);
          this.loadEventContacts(this.event.company_id || undefined);
        }
        this.isLoadingEvent = false;
      },
      error: (error) => {
        console.error('Error loading event:', error);
        this.toastService.error('Failed to load event details');
        this.isLoadingEvent = false;
      }
    });
  }

  loadCompanyData(companyId: string): void {
    this.isLoadingCompany = true;
    this.companyService.getPublicCompanyById(companyId).subscribe({
      next: (response) => {
        this.company = response;
        this.isLoadingCompany = false;
      },
      error: (error) => {
        console.error('Error loading company:', error);
        this.isLoadingCompany = false;
      }
    });
  }

  get isTrainingEvent(): boolean {
    return this.event?.category === 'training';
  }

  get isSocialEvent(): boolean {
    return this.event?.category === 'social';
  }

  get isCompetitionEvent(): boolean {
    return this.event?.category === 'competition' || (!this.event?.category && !this.isTrainingEvent && !this.isSocialEvent);
  }

  get isCustomPaymentEnabled(): boolean {
    // Custom payment is used when:
    // - Company has custom payment enabled
    // - Company does NOT have Stripe connected (active)
    return this.company?.custom_payment_enabled === true &&
           this.company?.stripe_account_status !== 'active';
  }

  loadTrainingPackages(): void {
    this.isLoadingPackages = true;
    this.trainingService.getPackagesWithAvailability({ event_id: this.eventId }).subscribe({
      next: (packages) => {
        this.trainingPackages = packages;
        this.isLoadingPackages = false;
      },
      error: (error) => {
        console.error('Error loading training packages:', error);
        this.toastService.error('Failed to load training packages');
        this.isLoadingPackages = false;
      }
    });
  }

  loadEventClasses(): void {
    this.isLoadingClasses = true;
    this.eventsService.getEventClasses({ event_id: this.eventId }).subscribe({
      next: (classes) => {
        console.log('Event classes loaded:', classes);
        this.eventClasses = classes;
        this.isLoadingClasses = false;
      },
      error: (error) => {
        console.error('Error loading event classes:', error);
        this.toastService.error('Failed to load event classes');
        this.isLoadingClasses = false;
      }
    });
  }

  loadEventServices(): void {
    this.isLoadingServices = true;
    this.eventsService.getEventServices({ event_id: this.eventId }).subscribe({
      next: (services) => {
        this.eventServices = services;
        this.isLoadingServices = false;
      },
      error: (error) => {
        console.error('Error loading event services:', error);
        this.toastService.error('Failed to load event services');
        this.isLoadingServices = false;
      }
    });
  }

  loadEventSponsors(companyId?: string): void {
    this.isLoadingSponsors = true;
    this.eventsService.getEventSponsors({ event_id: this.eventId }, companyId).subscribe({
      next: (sponsors) => {
        this.eventSponsors = sponsors;
        this.isLoadingSponsors = false;
      },
      error: (error) => {
        console.error('Error loading event sponsors:', error);
        this.isLoadingSponsors = false;
      }
    });
  }

  loadEventContacts(companyId?: string): void {
    this.isLoadingContacts = true;
    this.eventsService.getEventContacts({ event_id: this.eventId }, companyId).subscribe({
      next: (contacts) => {
        // Sort contacts by role_id with 0 first
        this.eventContacts = (contacts || []).sort((a, b) => {
          if (a.role_id === 0) return -1;
          if (b.role_id === 0) return 1;
          return a.role_id - b.role_id;
        });
        this.isLoadingContacts = false;
      },
      error: (error) => {
        console.error('Error loading event contacts:', error);
        this.isLoadingContacts = false;
      }
    });
  }

  loadAllHorses(): void {
    this.isLoadingHorses = true;
    // Load only the current user's horses
    this.horsesService.getHorses({ user_id: this.currentUserId }).subscribe({
      next: (horses: HorseResponse[]) => {
        this.allHorses = horses;
        this.isLoadingHorses = false;
      },
      error: (error: any) => {
        console.error('Error loading horses:', error);
        this.toastService.error('Failed to load horses');
        this.isLoadingHorses = false;
      }
    });
  }

  loadHorseRegistries(): void {
    this.staticService.getHorseRegistries().subscribe({
      next: (response: any) => {
        this.horseRegistries = response.data;
      },
      error: (error: any) => {
        console.error('Error loading horse registries:', error);
      }
    });
  }

  loadFamilyMembers(): void {
    // Start with the current user
    this.familyMembers = [{
      user_id: this.currentUserId,
      name: `${this.currentUserName} (Me)`
    }];

    // Load memberships to get family members
    this.membershipService.getUserMembership({}).subscribe({
      next: (memberships) => {
        memberships.forEach(membership => {
          if (membership.family && membership.family.members) {
            membership.family.members.forEach(member => {
              // Add family members that aren't the current user
              if (member.user.user_id !== this.currentUserId) {
                this.familyMembers.push({
                  user_id: member.user.user_id,
                  name: member.user.full_name || `${member.user.first_name} ${member.user.last_name}`
                });
              }
            });
          }
        });
      },
      error: (error) => {
        console.error('Error loading family members:', error);
      }
    });
  }

  loadUserPrices(): void {
    this.isLoadingPrices = true;
    this.eventsService.getUserEventPrices({ event_id: this.eventId }).subscribe({
      next: (response: UserPricesResponse) => {
        this.userPlanIds = response.user_plan_ids || [];
        console.log('User plan IDs:', this.userPlanIds);
        console.log('Price info response:', response);
        this.priceInfoMap.clear();

        // Build price info map keyed by "type-id"
        for (const priceInfo of response.prices) {
          const key = `${priceInfo.item_type}-${priceInfo.item_id}`;
          this.priceInfoMap.set(key, priceInfo);
        }

        console.log('User prices loaded:', this.priceInfoMap.size, 'items');
        this.isLoadingPrices = false;
      },
      error: (error) => {
        console.error('Error loading user prices:', error);
        this.isLoadingPrices = false;
      }
    });
  }

  getPriceInfo(itemType: 'class' | 'package' | 'service', itemId: number): PriceInfoResponse | null {
    const key = `${itemType}-${itemId}`;
    return this.priceInfoMap.get(key) || null;
  }

  getEffectivePrice(itemType: 'class' | 'package' | 'service', itemId: number, basePrice: number): number {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    return priceInfo ? priceInfo.effective_price : basePrice;
  }

  hasMemberDiscount(itemType: 'class' | 'package' | 'service', itemId: number): boolean {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    return priceInfo !== null && priceInfo.applied_membership !== null && priceInfo.savings_percent > 0;
  }

  getSavingsPercent(itemType: 'class' | 'package' | 'service', itemId: number): number {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    return priceInfo?.savings_percent || 0;
  }

  canGetMemberDiscount(itemType: 'class' | 'package' | 'service', itemId: number): boolean {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    return priceInfo !== null &&
           priceInfo.applied_membership === null &&
           priceInfo.purchasable_plans.length > 0;
  }

  getBestMemberPrice(itemType: 'class' | 'package' | 'service', itemId: number): number | null {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    if (!priceInfo || priceInfo.purchasable_plans.length === 0) {
      return null;
    }
    // Find the lowest discounted price among all purchasable plans
    const bestPlan = priceInfo.purchasable_plans.reduce((best, current) =>
      current.discounted_price < best.discounted_price ? current : best
    );
    // For packages, prices are in øre (cents), convert to kroner
    const price = bestPlan.discounted_price;
    return itemType === 'package' ? price / 100 : price;
  }

  getBestMemberSavings(itemType: 'class' | 'package' | 'service', itemId: number): number {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    if (!priceInfo || priceInfo.purchasable_plans.length === 0) {
      return 0;
    }
    // Find the highest savings percent among all purchasable plans
    const bestPlan = priceInfo.purchasable_plans.reduce((best, current) =>
      current.savings_percent > best.savings_percent ? current : best
    );
    return bestPlan.savings_percent;
  }

  // Membership requirement helpers
  isMembershipRequired(eventClass: EventClassResponse): boolean {
    return (eventClass.accepted_membership_plans?.length ?? 0) > 0;
  }

  hasRequiredMembership(eventClass: EventClassResponse): boolean {
    if (!this.isMembershipRequired(eventClass)) return true;
    return eventClass.accepted_membership_plans!.some(plan => this.userPlanIds.includes(plan.plan_id));
  }

  getMissingMembershipClasses(): ClassRegistration[] {
    return this.classRegistrations.filter(reg => {
      const ec = this.eventClasses.find(c => c.event_class_id === reg.event_class_id);
      return ec && this.isMembershipRequired(ec) && !this.hasRequiredMembership(ec);
    });
  }

  get hasMembershipIssues(): boolean {
    return this.getMissingMembershipClasses().length > 0;
  }

  getClassById(classId: number): EventClassResponse | undefined {
    return this.eventClasses.find(c => c.event_class_id === classId);
  }

  addClassRegistration(eventClass: EventClassResponse): void {
    console.log('Adding class registration:', eventClass);

    // Collapse all existing registrations (both classes and services)
    this.classRegistrations.forEach(reg => reg.expanded = false);
    this.serviceRegistrations.forEach(reg => reg.expanded = false);

    const basePrice = eventClass.price || 0;
    const effectivePrice = this.getEffectivePrice('class', eventClass.event_class_id, basePrice);

    // Always allow adding multiple registrations for the same class
    // User can register multiple horses in the same class
    // Backend validates: same rider+horse combo not allowed, max 2 riders per horse
    const registration: ClassRegistration = {
      event_class_id: eventClass.event_class_id,
      class_name: eventClass.class_name,
      user_id: this.currentUserId,
      user_name: this.currentUserName,
      horse_id: null,
      price: effectivePrice,
      base_price: basePrice,
      effective_price: effectivePrice,
      price_currency: eventClass.price_currency || this.event?.default_currency || 'NOK',
      expanded: true
    };
    this.classRegistrations.push(registration);
    console.log('Class registrations now:', this.classRegistrations);
  }

  removeClassRegistration(index: number): void {
    this.classRegistrations.splice(index, 1);
  }

  onHorseSelected(registration: ClassRegistration, horseId: number | string): void {
    // Convert to number if it's a string
    const numericHorseId = typeof horseId === 'string' ? parseInt(horseId, 10) : horseId;

    if (!numericHorseId || isNaN(numericHorseId)) {
      registration.horse_id = null;
      registration.horse_name = undefined;
      registration.error = undefined;
      return;
    }

    const horse = this.allHorses.find(h => h.horse_id === numericHorseId);
    if (horse) {
      registration.horse_id = numericHorseId;
      registration.horse_name = horse.name;
      registration.error = undefined;

      // Validate horse registry
      const eventClass = this.eventClasses.find(ec => ec.event_class_id === registration.event_class_id);
      if (eventClass && eventClass.req_horse_registration_id) {
        // If req_horse_registration_id is 3, allow all horses
        if (eventClass.req_horse_registration_id !== 3) {
          if (!horse.horse_registry_id || horse.horse_registry_id !== eventClass.req_horse_registration_id) {
            registration.error = 'This horse does not meet the registry requirements for this class';
            return;
          }
        }
      }

      // Check if same rider+horse combo already exists in cart for this class
      const duplicateInCart = this.classRegistrations.find(
        reg => reg !== registration &&
               reg.event_class_id === registration.event_class_id &&
               reg.user_id === registration.user_id &&
               reg.horse_id === numericHorseId
      );

      if (duplicateInCart) {
        registration.error = 'You already have a registration with this horse in this class';
      }
    }
  }

  onUserSelected(registration: ClassRegistration, userId: string): void {
    const selectedMember = this.familyMembers.find(m => m.user_id === userId);
    if (selectedMember) {
      registration.user_name = selectedMember.name;
    }
  }

  addServiceRegistration(eventService: EventServiceResponse): void {
    const availableSlots = this.getAvailableServiceSlots(eventService);
    const existingIndex = this.serviceRegistrations.findIndex(
      sr => sr.event_service_id === eventService.event_service_id && sr.user_id === this.currentUserId
    );

    if (existingIndex >= 0) {
      const currentReg = this.serviceRegistrations[existingIndex];
      // Check if we can add more
      if (currentReg.num_registrations < availableSlots) {
        currentReg.num_registrations++;
      }
      // Collapse all and expand only the existing one
      this.classRegistrations.forEach(reg => reg.expanded = false);
      this.serviceRegistrations.forEach((reg, idx) => reg.expanded = idx === existingIndex);
    } else {
      // Collapse all existing registrations (both classes and services)
      this.classRegistrations.forEach(reg => reg.expanded = false);
      this.serviceRegistrations.forEach(reg => reg.expanded = false);

      const basePrice = eventService.price || 0;
      const effectivePrice = this.getEffectivePrice('service', eventService.event_service_id, basePrice);

      const registration: ServiceRegistration = {
        event_service_id: eventService.event_service_id,
        service_name: eventService.event_service_name,
        user_id: this.currentUserId,
        user_name: this.currentUserName,
        num_registrations: 1,
        price_per_unit: effectivePrice,
        base_price_per_unit: basePrice,
        effective_price_per_unit: effectivePrice,
        price_currency: eventService.price_currency || this.event?.default_currency || 'NOK',
        expanded: true,
        max_slots: availableSlots
      };
      this.serviceRegistrations.push(registration);
    }
  }

  updateServiceQuantity(registration: ServiceRegistration, quantity: number): void {
    if (quantity > 0) {
      // Enforce max slots limit
      const maxAllowed = registration.max_slots || 999;
      registration.num_registrations = Math.min(quantity, maxAllowed);
    }
  }

  removeServiceRegistration(index: number): void {
    this.serviceRegistrations.splice(index, 1);
  }

  // ============================================================================
  // PACKAGE REGISTRATION (for Training Events)
  // ============================================================================

  addPackageRegistration(pkg: TrainingPackageResponse): void {
    // Check if this package is already in the cart for this user
    const existingIndex = this.packageRegistrations.findIndex(
      reg => reg.package_id === pkg.package_id && reg.user_id === this.currentUserId
    );

    if (existingIndex >= 0) {
      // Collapse all and expand the existing one
      this.packageRegistrations.forEach(reg => reg.expanded = false);
      this.serviceRegistrations.forEach(reg => reg.expanded = false);
      this.packageRegistrations[existingIndex].expanded = true;
      return;
    }

    // Collapse all existing registrations
    this.packageRegistrations.forEach(reg => reg.expanded = false);
    this.serviceRegistrations.forEach(reg => reg.expanded = false);

    const basePriceCents = pkg.price_cents || 0;
    // For packages, the price info is in cents
    const effectivePriceCents = this.getEffectivePrice('package', pkg.package_id, basePriceCents);

    const registration: PackageRegistration = {
      package_id: pkg.package_id,
      package_name: pkg.package_name,
      user_id: this.currentUserId,
      user_name: this.currentUserName,
      horse_id: null,
      session_count: pkg.session_count,
      session_type: pkg.session_type,
      price: effectivePriceCents / 100,
      base_price: basePriceCents / 100,
      effective_price: effectivePriceCents / 100,
      price_currency: pkg.currency || this.event?.default_currency || 'NOK',
      expanded: true
    };
    this.packageRegistrations.push(registration);
  }

  removePackageRegistration(index: number): void {
    this.packageRegistrations.splice(index, 1);
  }

  onPackageHorseSelected(registration: PackageRegistration, horseId: number | string): void {
    const numericHorseId = typeof horseId === 'string' ? parseInt(horseId, 10) : horseId;

    if (!numericHorseId || isNaN(numericHorseId)) {
      registration.horse_id = null;
      registration.horse_name = undefined;
      return;
    }

    const horse = this.allHorses.find(h => h.horse_id === numericHorseId);
    if (horse) {
      registration.horse_id = numericHorseId;
      registration.horse_name = horse.name;
    }
  }

  onPackageUserSelected(registration: PackageRegistration, userId: string): void {
    const selectedMember = this.familyMembers.find(m => m.user_id === userId);
    if (selectedMember) {
      registration.user_name = selectedMember.name;
    }
  }

  togglePackageRegistration(registration: PackageRegistration): void {
    const wasExpanded = registration.expanded;
    this.packageRegistrations.forEach(reg => reg.expanded = false);
    this.serviceRegistrations.forEach(reg => reg.expanded = false);
    registration.expanded = !wasExpanded;
  }

  isPackageFull(pkg: TrainingPackageResponse): boolean {
    // For training packages, availability is based on day capacity
    return pkg.is_available === false;
  }

  getPackageAvailabilityLabel(pkg: TrainingPackageResponse): string {
    if (!pkg.is_available) {
      return 'Full';
    }
    if (pkg.slots_left != null && pkg.slots_left <= 3) {
      return `Few slots left (${pkg.slots_left})`;
    }
    return 'Available';
  }

  getPackageAvailabilityClass(pkg: TrainingPackageResponse): string {
    if (!pkg.is_available) {
      return 'badge-light-danger';
    }
    if (pkg.slots_left != null && pkg.slots_left <= 3) {
      return 'badge-light-warning';
    }
    return 'badge-light-success';
  }

  getSessionTypeLabel(type: string): string {
    switch (type) {
      case 'individual': return 'Individual Sessions';
      case 'group': return 'Group Sessions';
      case 'both': return 'Individual & Group';
      default: return type;
    }
  }

  openAddHorseModal(): void {
    this.showAddHorseModal = true;
    this.addHorseForm.reset();
    this.addHorseSubmitted = false;
    // Reset image state
    this.horseImageFile = null;
    this.pedigreeFile = null;
    this.vaccinationCardFile = null;
    this.horseImagePreview = null;
    this.pedigreePreview = null;
    this.vaccinationCardPreview = null;
  }

  closeAddHorseModal(): void {
    this.showAddHorseModal = false;
  }

  openTermsModal(): void {
    this.showTermsModal = true;
  }

  closeTermsModal(): void {
    this.showTermsModal = false;
  }

  toggleDescription(): void {
    this.descriptionExpanded = !this.descriptionExpanded;
  }

  submitNewHorse(): void {
    this.addHorseSubmitted = true;

    if (this.addHorseForm.invalid) {
      this.toastService.error('Please fill in all required fields');
      return;
    }

    const formValue = this.addHorseForm.value;

    // Build horse data with proper types
    const horseData: any = {
      name: formValue.name?.trim(),
      gender: formValue.gender !== null ? Number(formValue.gender) : null,
      date_of_birth: formValue.date_of_birth || null,
      passport_id: formValue.passport_id?.trim() || null,
      horse_registry_id: formValue.horse_registry_id ? Number(formValue.horse_registry_id) : null,
      breed: formValue.breed?.trim() || null,
      color: formValue.color?.trim() || null,
      vaccination_date: formValue.vaccination_date || null,
      next_vaccination_date: formValue.next_vaccination_date || null,
      image_url: '',
      pedigree_image_url: '',
      vaccination_card_url: ''
    };

    console.log('Submitting horse data:', horseData);

    this.horsesService.postHorse(horseData).subscribe({
      next: async (newHorse: HorseResponse) => {
        // Upload images sequentially
        let horse = newHorse;

        if (this.horseImageFile) {
          try {
            horse = await this.horsesService.uploadHorseImage({
              horse_id: newHorse.horse_id,
              image_type: 'horse_image',
              image: this.horseImageFile
            }).toPromise() || horse;
          } catch (e) {
            console.error('Horse image upload failed:', e);
          }
        }

        if (this.pedigreeFile) {
          try {
            horse = await this.horsesService.uploadHorseImage({
              horse_id: newHorse.horse_id,
              image_type: 'pedigree_image',
              image: this.pedigreeFile
            }).toPromise() || horse;
          } catch (e) {
            console.error('Pedigree image upload failed:', e);
          }
        }

        if (this.vaccinationCardFile) {
          try {
            horse = await this.horsesService.uploadHorseImage({
              horse_id: newHorse.horse_id,
              image_type: 'vaccination_image',
              image: this.vaccinationCardFile
            }).toPromise() || horse;
          } catch (e) {
            console.error('Vaccination image upload failed:', e);
          }
        }

        this.toastService.success('Horse added successfully');
        this.allHorses.push(horse);
        this.closeAddHorseModal();
      },
      error: (error: any) => {
        console.error('Error adding horse:', error);
        this.toastService.error(error.error?.reason || 'Failed to add horse');
      }
    });
  }

  // Image handlers for add horse modal
  onHorseImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.horseImageFile = input.files[0];
      this.horseImagePreview = URL.createObjectURL(this.horseImageFile);
    }
  }

  onPedigreeSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.pedigreeFile = input.files[0];
      this.pedigreePreview = URL.createObjectURL(this.pedigreeFile);
    }
  }

  onVaccinationCardSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.vaccinationCardFile = input.files[0];
      this.vaccinationCardPreview = URL.createObjectURL(this.vaccinationCardFile);
    }
  }

  removeHorseImage(): void {
    this.horseImageFile = null;
    this.horseImagePreview = null;
  }

  removePedigree(): void {
    this.pedigreeFile = null;
    this.pedigreePreview = null;
  }

  removeVaccinationCard(): void {
    this.vaccinationCardFile = null;
    this.vaccinationCardPreview = null;
  }

  // Horse Registry Search Methods
  openHorseRegistryModal(registration: any): void {
    this.activeHorseRegistration = registration;
    this.showHorseRegistryModal = true;
    this.horseSearchQuery = '';
    this.horseSearchResults = [];
  }

  closeHorseRegistryModal(): void {
    this.showHorseRegistryModal = false;
    this.activeHorseRegistration = null;
    this.horseSearchQuery = '';
    this.horseSearchResults = [];
  }

  searchHorseRegistry(): void {
    if (!this.horseSearchQuery || this.horseSearchQuery.length < 2) {
      this.horseSearchResults = [];
      return;
    }

    this.isSearchingHorses = true;
    this.horsesService.searchHorses(this.horseSearchQuery).subscribe({
      next: (horses: HorseResponse[]) => {
        this.horseSearchResults = horses;
        this.isSearchingHorses = false;
      },
      error: (error: any) => {
        console.error('Error searching horses:', error);
        this.horseSearchResults = [];
        this.isSearchingHorses = false;
      }
    });
  }

  selectHorseFromRegistry(horse: HorseResponse): void {
    if (this.activeHorseRegistration) {
      this.activeHorseRegistration.horse_id = horse.horse_id;
      this.activeHorseRegistration.horse_name = horse.name;
      this.activeHorseRegistration.error = undefined;

      // Validate horse registry if required (for class registrations)
      if (this.activeHorseRegistration.event_class_id) {
        const eventClass = this.eventClasses.find(ec => ec.event_class_id === this.activeHorseRegistration.event_class_id);
        if (eventClass && eventClass.req_horse_registration_id && eventClass.req_horse_registration_id !== 3) {
          if (!horse.horse_registry_id || horse.horse_registry_id !== eventClass.req_horse_registration_id) {
            this.activeHorseRegistration.error = 'This horse does not meet the registry requirements for this class';
          }
        }
      }
    }
    this.closeHorseRegistryModal();
  }

  isMyHorse(horse: HorseResponse): boolean {
    return this.allHorses.some(h => h.horse_id === horse.horse_id);
  }

  isMyHorseById(horseId: number): boolean {
    return this.allHorses.some(h => h.horse_id === horseId);
  }

  clearHorseSelection(registration: any): void {
    registration.horse_id = null;
    registration.horse_name = undefined;
    registration.error = undefined;
  }

  getTotalPrice(): number {
    let total = 0;

    // Add class registration prices
    this.classRegistrations.forEach(reg => {
      total += reg.price;
    });

    // Add service registration prices
    this.serviceRegistrations.forEach(reg => {
      total += reg.price_per_unit * reg.num_registrations;
    });

    // Add package registration prices
    this.packageRegistrations.forEach(reg => {
      total += reg.price;
    });

    return total;
  }

  getCurrency(): string {
    if (this.classRegistrations.length > 0) {
      return this.classRegistrations[0].price_currency;
    }
    if (this.serviceRegistrations.length > 0) {
      return this.serviceRegistrations[0].price_currency;
    }
    if (this.packageRegistrations.length > 0) {
      return this.packageRegistrations[0].price_currency;
    }
    return this.event?.default_currency || 'NOK';
  }

  canSubmit(): boolean {
    if (this.isSubmitting) return false;

    // For training events, check package registrations
    if (this.isTrainingEvent) {
      if (this.packageRegistrations.length === 0 && this.serviceRegistrations.length === 0) {
        return false;
      }
      // Package registrations don't require horse selection (optional)
      return true;
    }

    // For competition events, check class and service registrations
    if (this.classRegistrations.length === 0 && this.serviceRegistrations.length === 0) {
      return false;
    }

    // Check if all class registrations have horses selected
    for (const reg of this.classRegistrations) {
      if (!reg.horse_id || reg.error) {
        return false;
      }
    }

    // Check for membership requirement issues
    if (this.hasMembershipIssues) {
      return false;
    }

    return true;
  }

  async submitRegistrations(): Promise<void> {
    console.log('Submit button clicked!');
    console.log('Can submit?', this.canSubmit());
    console.log('Class registrations:', this.classRegistrations);
    console.log('Service registrations:', this.serviceRegistrations);
    console.log('Package registrations:', this.packageRegistrations);
    console.log('Event payment_enabled:', this.event?.payment_enabled);
    console.log('Event payment_timing:', this.event?.payment_timing);
    console.log('Company loaded:', !!this.company);
    console.log('Company custom_payment_enabled:', this.company?.custom_payment_enabled);
    console.log('Company stripe_account_status:', this.company?.stripe_account_status);
    console.log('isCustomPaymentEnabled:', this.isCustomPaymentEnabled);

    if (!this.canSubmit()) {
      this.toastService.error('Please complete all registrations');
      return;
    }

    // If payment is enabled and company hasn't loaded yet, wait for it
    if (this.event?.payment_enabled && !this.company && this.event?.company_id) {
      console.log('Waiting for company data to load...');
      this.isSubmitting = true;
      try {
        await firstValueFrom(this.companyService.getPublicCompanyById(this.event.company_id))
          .then(response => {
            this.company = response;
            console.log('Company loaded:', this.company);
          });
      } catch (error) {
        console.error('Failed to load company:', error);
      }
    }

    this.isSubmitting = true;

    try {
      // Handle training event registration differently
      if (this.isTrainingEvent) {
        if (this.event?.payment_enabled) {
          if (this.isCustomPaymentEnabled) {
            // Custom payment flow - create registrations then show payment instructions
            await this.handleTrainingCustomPaymentRegistration();
          } else if (this.event.payment_timing === 'immediate') {
            await this.handleTrainingImmediatePayment();
          } else {
            await this.handleTrainingPayLaterRegistration();
          }
        } else {
          await this.handleTrainingFreeRegistration();
        }
      } else {
        // Competition/Social event registration
        if (this.event?.payment_enabled) {
          if (this.isCustomPaymentEnabled) {
            // Custom payment flow - create registrations then show payment instructions
            await this.handleCustomPaymentRegistration();
          } else if (this.event.payment_timing === 'immediate') {
            await this.handleImmediatePayment();
          } else {
            await this.handlePayLaterRegistration();
          }
        } else {
          await this.handleFreeRegistration();
        }
      }
    } catch (error: any) {
      console.error('Error submitting registrations:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.status,
        error: error?.error
      });

      // Backend returns errors with 'reason' field from response_error(reason="...")
      const errorMessage = error?.error?.reason || error?.error?.message || error?.message || 'Failed to complete registration';
      const errorStr = (errorMessage || '').toLowerCase();
      if (errorStr.includes('membership is required')) {
        this.toastService.error(this.translateService.instant('eventRegistration.membershipRequiredDesc'));
      } else {
        this.toastService.error(errorMessage);
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  private async handleImmediatePayment(): Promise<void> {
    // De-duplicate class registrations
    const uniqueClassRegs = new Map<string, any>();
    for (const reg of this.classRegistrations) {
      const key = `${reg.event_class_id}-${reg.user_id}-${reg.horse_id}`;
      if (!uniqueClassRegs.has(key)) {
        uniqueClassRegs.set(key, {
          event_class_id: reg.event_class_id,
          user_id: reg.user_id,
          horse_id: reg.horse_id!,
          notes: reg.notes || undefined
        });
      }
    }

    // De-duplicate service registrations (combine quantities for same service+user)
    const uniqueServiceRegs = new Map<string, any>();
    for (const reg of this.serviceRegistrations) {
      const key = `${reg.event_service_id}-${reg.user_id}`;
      if (uniqueServiceRegs.has(key)) {
        uniqueServiceRegs.get(key).num_registrations += reg.num_registrations;
      } else {
        uniqueServiceRegs.set(key, {
          event_service_id: reg.event_service_id,
          user_id: reg.user_id,
          num_registrations: reg.num_registrations,
          notes: reg.notes || undefined
        });
      }
    }

    const checkoutParams = {
      event_id: this.eventId,
      class_registrations: Array.from(uniqueClassRegs.values()),
      service_registrations: Array.from(uniqueServiceRegs.values())
    };

    console.log('Checkout params (de-duplicated):', JSON.stringify(checkoutParams));

    const response = await firstValueFrom(
      this.paymentService.createCheckoutSession(checkoutParams)
    );

    // If free order (membership discount made total 0), go straight to success
    if (response.free_order) {
      this.router.navigate(['/event-registration', this.eventId, 'success'], {
        queryParams: { order_id: response.order_id }
      });
      return;
    }

    // Redirect to Stripe Checkout
    window.location.href = response.checkout_url;
  }

  private async handlePayLaterRegistration(): Promise<void> {
    // De-duplicate class registrations
    const uniqueClassRegs = new Map<string, any>();
    for (const reg of this.classRegistrations) {
      const key = `${reg.event_class_id}-${reg.user_id}-${reg.horse_id}`;
      if (!uniqueClassRegs.has(key)) {
        uniqueClassRegs.set(key, {
          event_class_id: reg.event_class_id,
          user_id: reg.user_id,
          horse_id: reg.horse_id!,
          notes: reg.notes || undefined
        });
      }
    }

    // De-duplicate service registrations (combine quantities for same service+user)
    const uniqueServiceRegs = new Map<string, any>();
    for (const reg of this.serviceRegistrations) {
      const key = `${reg.event_service_id}-${reg.user_id}`;
      if (uniqueServiceRegs.has(key)) {
        uniqueServiceRegs.get(key).num_registrations += reg.num_registrations;
      } else {
        uniqueServiceRegs.set(key, {
          event_service_id: reg.event_service_id,
          user_id: reg.user_id,
          num_registrations: reg.num_registrations,
          notes: reg.notes || undefined
        });
      }
    }

    const payLaterParams = {
      event_id: this.eventId,
      class_registrations: Array.from(uniqueClassRegs.values()),
      service_registrations: Array.from(uniqueServiceRegs.values())
    };

    console.log('Creating pay-later registration (de-duplicated):', payLaterParams);

    const response = await firstValueFrom(
      this.paymentService.createPayLaterRegistration(payLaterParams)
    );

    // Track successful registration with Google Analytics
    const totalAmount = this.getTotalPrice();
    this.analyticsService.trackEventRegistrationComplete(
      this.eventId,
      this.event?.event_name || 'Unknown Event',
      totalAmount,
      this.event?.default_currency || 'NOK'
    );

    this.toastService.success('Registration completed! Payment will be collected later.');
    this.router.navigate(['/event-details', this.eventId]);
  }

  private async handleFreeRegistration(): Promise<void> {
    // Submit class registrations
    for (const classReg of this.classRegistrations) {
      console.log('Submitting class registration:', {
        event_class_id: classReg.event_class_id,
        user_id: classReg.user_id,
        horse_id: classReg.horse_id
      });

      await firstValueFrom(this.eventsService.registerForEventClass({
        event_class_id: classReg.event_class_id,
        user_id: classReg.user_id,
        horse_id: classReg.horse_id!,
        notes: classReg.notes || undefined
      }));

      console.log('Class registration submitted successfully');
    }

    // Submit service registrations
    for (const serviceReg of this.serviceRegistrations) {
      console.log('Submitting service registration:', {
        event_id: this.eventId,
        event_service_id: serviceReg.event_service_id,
        user_id: serviceReg.user_id,
        num_registrations: serviceReg.num_registrations
      });

      await firstValueFrom(this.eventsService.registerForEventService({
        event_id: this.eventId,
        event_service_id: serviceReg.event_service_id,
        user_id: serviceReg.user_id,
        num_registrations: serviceReg.num_registrations,
        notes: serviceReg.notes || undefined
      }));

      console.log('Service registration submitted successfully');
    }

    console.log('All registrations completed successfully');

    // Track successful registration with Google Analytics
    const totalAmount = this.getTotalPrice();
    this.analyticsService.trackEventRegistrationComplete(
      this.eventId,
      this.event?.event_name || 'Unknown Event',
      totalAmount,
      this.event?.default_currency || 'NOK'
    );

    this.toastService.success('Registration completed successfully!');
    this.router.navigate(['/event-details', this.eventId]);
  }

  private async handleCustomPaymentRegistration(): Promise<void> {
    // De-duplicate class registrations
    const uniqueClassRegs = new Map<string, any>();
    for (const reg of this.classRegistrations) {
      const key = `${reg.event_class_id}-${reg.user_id}-${reg.horse_id}`;
      if (!uniqueClassRegs.has(key)) {
        uniqueClassRegs.set(key, {
          event_class_id: reg.event_class_id,
          user_id: reg.user_id,
          horse_id: reg.horse_id!,
          notes: reg.notes || undefined
        });
      }
    }

    // De-duplicate service registrations (combine quantities for same service+user)
    const uniqueServiceRegs = new Map<string, any>();
    for (const reg of this.serviceRegistrations) {
      const key = `${reg.event_service_id}-${reg.user_id}`;
      if (uniqueServiceRegs.has(key)) {
        uniqueServiceRegs.get(key).num_registrations += reg.num_registrations;
      } else {
        uniqueServiceRegs.set(key, {
          event_service_id: reg.event_service_id,
          user_id: reg.user_id,
          num_registrations: reg.num_registrations,
          notes: reg.notes || undefined
        });
      }
    }

    const payLaterParams = {
      event_id: this.eventId,
      class_registrations: Array.from(uniqueClassRegs.values()),
      service_registrations: Array.from(uniqueServiceRegs.values())
    };

    console.log('Creating custom payment registration:', payLaterParams);

    // Create registrations with pay-later endpoint (creates pending payment status)
    await firstValueFrom(
      this.paymentService.createPayLaterRegistration(payLaterParams)
    );

    // Show custom payment instructions modal
    this.showCustomPaymentModal();
  }

  private showCustomPaymentModal(): void {
    const modalRef = this.modalService.open(CustomPaymentModal, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });

    modalRef.componentInstance.eventName = this.event?.event_name || '';
    modalRef.componentInstance.paymentMethodName = this.company?.custom_payment_method_name || 'Manual Payment';
    modalRef.componentInstance.paymentInstructions = this.company?.custom_payment_instructions || '';
    modalRef.componentInstance.totalAmount = this.getTotalPrice();
    modalRef.componentInstance.currency = this.getCurrency();

    modalRef.result.then(() => {
      // User acknowledged, navigate to event details
      this.router.navigate(['/event-details', this.eventId]);
    }).catch(() => {
      // Modal dismissed - still navigate
      this.router.navigate(['/event-details', this.eventId]);
    });
  }

  // ============================================================================
  // TRAINING EVENT REGISTRATION HANDLERS
  // ============================================================================

  private async handleTrainingImmediatePayment(): Promise<void> {
    // De-duplicate package registrations
    const uniquePackageRegs = new Map<string, any>();
    for (const reg of this.packageRegistrations) {
      const key = `${reg.package_id}-${reg.user_id}`;
      if (!uniquePackageRegs.has(key)) {
        uniquePackageRegs.set(key, {
          package_id: reg.package_id,
          user_id: reg.user_id,
          horse_id: reg.horse_id || undefined,
          notes: reg.notes || undefined
        });
      }
    }

    // De-duplicate service registrations
    const uniqueServiceRegs = new Map<string, any>();
    for (const reg of this.serviceRegistrations) {
      const key = `${reg.event_service_id}-${reg.user_id}`;
      if (uniqueServiceRegs.has(key)) {
        uniqueServiceRegs.get(key).num_registrations += reg.num_registrations;
      } else {
        uniqueServiceRegs.set(key, {
          event_service_id: reg.event_service_id,
          user_id: reg.user_id,
          num_registrations: reg.num_registrations,
          notes: reg.notes || undefined
        });
      }
    }

    const checkoutParams = {
      event_id: this.eventId,
      package_registrations: Array.from(uniquePackageRegs.values()),
      service_registrations: Array.from(uniqueServiceRegs.values())
    };

    console.log('Training checkout params:', JSON.stringify(checkoutParams));

    const response = await firstValueFrom(
      this.paymentService.createTrainingCheckoutSession(checkoutParams)
    );

    if (response.free_order) {
      this.router.navigate(['/event-registration', this.eventId, 'success'], {
        queryParams: { order_id: response.order_id }
      });
      return;
    }

    window.location.href = response.checkout_url;
  }

  private async handleTrainingPayLaterRegistration(): Promise<void> {
    // De-duplicate package registrations
    const uniquePackageRegs = new Map<string, any>();
    for (const reg of this.packageRegistrations) {
      const key = `${reg.package_id}-${reg.user_id}`;
      if (!uniquePackageRegs.has(key)) {
        uniquePackageRegs.set(key, {
          package_id: reg.package_id,
          user_id: reg.user_id,
          horse_id: reg.horse_id || undefined,
          notes: reg.notes || undefined
        });
      }
    }

    // De-duplicate service registrations
    const uniqueServiceRegs = new Map<string, any>();
    for (const reg of this.serviceRegistrations) {
      const key = `${reg.event_service_id}-${reg.user_id}`;
      if (uniqueServiceRegs.has(key)) {
        uniqueServiceRegs.get(key).num_registrations += reg.num_registrations;
      } else {
        uniqueServiceRegs.set(key, {
          event_service_id: reg.event_service_id,
          user_id: reg.user_id,
          num_registrations: reg.num_registrations,
          notes: reg.notes || undefined
        });
      }
    }

    const payLaterParams = {
      event_id: this.eventId,
      package_registrations: Array.from(uniquePackageRegs.values()),
      service_registrations: Array.from(uniqueServiceRegs.values())
    };

    console.log('Training pay-later params:', JSON.stringify(payLaterParams));

    const response = await firstValueFrom(
      this.paymentService.createTrainingPayLaterRegistration(payLaterParams)
    );

    // Track successful registration with Google Analytics
    const totalAmount = this.getTotalPrice();
    this.analyticsService.trackEventRegistrationComplete(
      this.eventId,
      this.event?.event_name || 'Unknown Event',
      totalAmount,
      this.event?.default_currency || 'NOK'
    );

    this.toastService.success('Registration completed! Payment will be collected later.');
    this.router.navigate(['/event-details', this.eventId]);
  }

  private async handleTrainingFreeRegistration(): Promise<void> {
    // Submit package registrations
    for (const pkgReg of this.packageRegistrations) {
      console.log('Submitting training registration:', {
        event_id: this.eventId,
        package_id: pkgReg.package_id,
        user_id: pkgReg.user_id,
        horse_id: pkgReg.horse_id,
        notes: pkgReg.notes
      });

      await firstValueFrom(this.trainingService.createRegistration({
        event_id: this.eventId,
        package_id: pkgReg.package_id,
        user_id: pkgReg.user_id,
        horse_id: pkgReg.horse_id || undefined,
        notes: pkgReg.notes || undefined
      }));

      console.log('Training registration submitted successfully');
    }

    // Submit service registrations
    for (const serviceReg of this.serviceRegistrations) {
      await firstValueFrom(this.eventsService.registerForEventService({
        event_id: this.eventId,
        event_service_id: serviceReg.event_service_id,
        user_id: serviceReg.user_id,
        num_registrations: serviceReg.num_registrations,
        notes: serviceReg.notes || undefined
      }));
    }

    console.log('All training registrations completed successfully');

    // Track successful registration with Google Analytics
    const totalAmount = this.getTotalPrice();
    this.analyticsService.trackEventRegistrationComplete(
      this.eventId,
      this.event?.event_name || 'Unknown Event',
      totalAmount,
      this.event?.default_currency || 'NOK'
    );

    this.toastService.success('Registration completed successfully!');
    this.router.navigate(['/event-details', this.eventId]);
  }

  private async handleTrainingCustomPaymentRegistration(): Promise<void> {
    // De-duplicate package registrations
    const uniquePackageRegs = new Map<string, any>();
    for (const reg of this.packageRegistrations) {
      const key = `${reg.package_id}-${reg.user_id}`;
      if (!uniquePackageRegs.has(key)) {
        uniquePackageRegs.set(key, {
          package_id: reg.package_id,
          user_id: reg.user_id,
          horse_id: reg.horse_id || undefined,
          notes: reg.notes || undefined
        });
      }
    }

    // De-duplicate service registrations
    const uniqueServiceRegs = new Map<string, any>();
    for (const reg of this.serviceRegistrations) {
      const key = `${reg.event_service_id}-${reg.user_id}`;
      if (uniqueServiceRegs.has(key)) {
        uniqueServiceRegs.get(key).num_registrations += reg.num_registrations;
      } else {
        uniqueServiceRegs.set(key, {
          event_service_id: reg.event_service_id,
          user_id: reg.user_id,
          num_registrations: reg.num_registrations,
          notes: reg.notes || undefined
        });
      }
    }

    const payLaterParams = {
      event_id: this.eventId,
      package_registrations: Array.from(uniquePackageRegs.values()),
      service_registrations: Array.from(uniqueServiceRegs.values())
    };

    console.log('Training custom payment params:', JSON.stringify(payLaterParams));

    // Create registrations with pay-later endpoint (creates pending payment status)
    await firstValueFrom(
      this.paymentService.createTrainingPayLaterRegistration(payLaterParams)
    );

    // Show custom payment instructions modal
    this.showCustomPaymentModal();
  }

  getAvailableUsers(): {user_id: string, name: string}[] {
    return [{ user_id: this.currentUserId, name: this.currentUserName }];
  }

  isClassFull(eventClass: EventClassResponse): boolean {
    if (!eventClass.max_num_registrations) return false;
    const currentRegistrations = eventClass.event_class_registrations?.length || 0;
    return currentRegistrations >= eventClass.max_num_registrations;
  }

  isServiceFull(eventService: EventServiceResponse): boolean {
    if (!eventService.max_num_registrations) return false;
    const currentRegistrations = eventService.event_service_registrations?.reduce(
      (sum, reg) => sum + (reg.num_registrations || 0), 0
    ) || 0;
    return currentRegistrations >= eventService.max_num_registrations;
  }

  getAvailableServiceSlots(eventService: EventServiceResponse): number {
    if (!eventService.max_num_registrations) return 999;
    const currentRegistrations = eventService.event_service_registrations?.reduce(
      (sum, reg) => sum + (reg.num_registrations || 0), 0
    ) || 0;
    return eventService.max_num_registrations - currentRegistrations;
  }

  getGoogleMapsLink(event: EventResponse): string {
    const address = `${event.address}, ${event.city}, ${event.country}`;
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  }

  shouldAnimateSponsors(): boolean {
    return this.eventSponsors.length > 3;
  }

  toggleClassRegistration(registration: ClassRegistration): void {
    const wasExpanded = registration.expanded;

    // Collapse all registrations (both classes and services)
    this.classRegistrations.forEach(reg => reg.expanded = false);
    this.serviceRegistrations.forEach(reg => reg.expanded = false);

    // Toggle this one (if it was collapsed, expand it; if it was expanded, keep it collapsed)
    registration.expanded = !wasExpanded;
  }

  toggleServiceRegistration(registration: ServiceRegistration): void {
    const wasExpanded = registration.expanded;

    // Collapse all registrations (both classes and services)
    this.classRegistrations.forEach(reg => reg.expanded = false);
    this.serviceRegistrations.forEach(reg => reg.expanded = false);

    // Toggle this one (if it was collapsed, expand it; if it was expanded, keep it collapsed)
    registration.expanded = !wasExpanded;
  }

  openMembershipPurchaseModal(itemType: 'class' | 'package' | 'service', itemId: number, itemName: string, basePrice: number, currency: string): void {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    if (!priceInfo || priceInfo.purchasable_plans.length === 0) {
      return;
    }

    // For packages, convert discounted_price from øre to kroner
    const purchasablePlans = itemType === 'package'
      ? priceInfo.purchasable_plans.map(plan => ({
          ...plan,
          discounted_price: plan.discounted_price / 100
        }))
      : priceInfo.purchasable_plans;

    const modalRef = this.modalService.open(MembershipPurchaseModal, {
      size: 'lg',
      centered: true
    });

    modalRef.componentInstance.itemName = itemName;
    modalRef.componentInstance.basePrice = basePrice;
    modalRef.componentInstance.currency = currency;
    modalRef.componentInstance.purchasablePlans = purchasablePlans;
    modalRef.componentInstance.eventId = this.eventId;
    modalRef.componentInstance.returnUrl = `/event/${this.eventId}/register`;

    modalRef.result.then(
      (result) => {
        if (result?.action === 'purchase') {
          console.log('User selected membership plan:', result.plan);
        }
      },
      () => {
        // Modal dismissed - user clicked "Maybe Later"
      }
    );
  }

  openRequiredMembershipPurchase(eventClass: EventClassResponse): void {
    // Try using existing priceInfo purchasable plans first
    const priceInfo = this.getPriceInfo('class', eventClass.event_class_id);
    if (priceInfo && priceInfo.purchasable_plans.length > 0) {
      this.openMembershipPurchaseModal('class', eventClass.event_class_id, eventClass.class_name, eventClass.price || 0, eventClass.price_currency || this.event?.default_currency || 'NOK');
      return;
    }

    // Fallback: open modal with accepted_membership_plans
    if (!eventClass.accepted_membership_plans || eventClass.accepted_membership_plans.length === 0) {
      this.toastService.info(this.translateService.instant('eventRegistration.membershipRequiredDesc'));
      return;
    }

    const purchasablePlans = eventClass.accepted_membership_plans.map(plan => ({
      plan_id: plan.plan_id,
      plan_name: plan.name,
      plan_price_cents: plan.price_cents,
      discounted_price: eventClass.price || 0,
      savings_percent: 0
    }));

    const modalRef = this.modalService.open(MembershipPurchaseModal, {
      size: 'lg',
      centered: true
    });

    modalRef.componentInstance.itemName = eventClass.class_name;
    modalRef.componentInstance.basePrice = eventClass.price || 0;
    modalRef.componentInstance.currency = eventClass.price_currency || this.event?.default_currency || 'NOK';
    modalRef.componentInstance.purchasablePlans = purchasablePlans;
    modalRef.componentInstance.eventId = this.eventId;
    modalRef.componentInstance.returnUrl = `/event/${this.eventId}/register`;

    modalRef.result.then(
      (result) => {
        if (result?.action === 'purchase') {
          console.log('User selected membership plan:', result.plan);
        }
      },
      () => {}
    );
  }

  // Classes grouped by date for better organization
  getClassesByDate(): { date: string; classes: EventClassResponse[] }[] {
    const grouped = new Map<string, EventClassResponse[]>();

    // Sort classes by start time first
    const sortedClasses = [...this.eventClasses].sort((a, b) => {
      const timeA = a.start_date_time ? new Date(a.start_date_time).getTime() : Infinity;
      const timeB = b.start_date_time ? new Date(b.start_date_time).getTime() : Infinity;
      return timeA - timeB;
    });

    for (const eventClass of sortedClasses) {
      let dateKey: string;
      if (eventClass.start_date_time) {
        const date = new Date(eventClass.start_date_time);
        dateKey = date.toISOString().split('T')[0];
      } else {
        dateKey = 'unscheduled';
      }

      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(eventClass);
    }

    // Convert to array and sort by date
    return Array.from(grouped.entries())
      .sort((a, b) => {
        if (a[0] === 'unscheduled') return 1;
        if (b[0] === 'unscheduled') return -1;
        return a[0].localeCompare(b[0]);
      })
      .map(([date, classes]) => ({ date, classes }));
  }

  formatScheduleDate(dateKey: string): string {
    if (dateKey === 'unscheduled') {
      return this.translateService.instant('eventRegistration.timeToBeAnnounced');
    }
    const date = new Date(dateKey + 'T00:00:00');
    const locale = this.translateService.currentLang === 'no' ? 'nb-NO' : 'en-GB';
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    return date.toLocaleDateString(locale, options);
  }
}
